import { useState } from 'react';
import {
  ConfirmationDialogV2,
  ErpDataTableV2,
  ErpInputTableV2,
  LoadingOverlayV2,
  BulkDeleteDialogV4
} from '@/components/custom/erp';
import { getSupplierReturnReceiptColumns, getSupplierReturnReceiptDetailColumns } from './cols-definition';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { PhieuNhapHangBanTraLaiSearchProvider } from './context';
import { initialSearchValues, SearchFormValues } from './schema';
import { useSearchFieldStates, useSearchTable } from './hooks';
import { useCRUD, useForms, usePatch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

const DETAIL_ACTION_ORDER = ['export', 'pin'] as const;
const noop = () => {};

function PhieuNhapHangBanTraLaiContent() {
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const searchFieldState = useSearchFieldStates();
  const {
    data,
    isLoading,
    isDetailLoading,
    fetchDetail,
    detail,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    totalItems,
    currentPage,
    handlePageChange,
    sumTotal,
    searchData,
    isFiltered,
    isSearching
  } = useCRUD<BanHangRecord, BanHangPayload>({
    endpoint: QUERY_KEYS.PHIEU_NHAP_HANG_BAN_TRA_LAI,
    sumField: 't_tt_nt'
  });
  const { patchItem } = usePatch({ endpoint: `${QUERY_KEYS.HOA_DON_BAN_HANG}/chi-tiet-hoa-don` });
  const {
    showForm,
    showDelete,
    showSearch,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    showBulkDelete,
    handleCloseBulkDelete,
    handleRowClick,
    clearSelection,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleSearch,
    handleCloseSearch
  } = useForms(fetchDetail);

  const {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering
  } = useSearchTable({
    searchData,
    searchParams,
    setSearchParams,
    loading: isLoading,
    handleViewClick,
    sumTotal,
    isFiltered,
    handlePageChange
  });

  const handleSearchSubmit = async (filters: SearchFormValues) => {
    await handleInitialSearch(filters);
  };

  const handleDelete = async () => {
    if (!selectedObj) return;
    await deleteItem(selectedObj.uuid);
    selectedObj.chi_tiet_data.forEach((item: BanHangRecord) => {
      patchItem(item.line_hd.uuid, { sl_cl: Number(item.line_hd.sl_cl) + Number(item.so_luong) });
    });
    clearSelection();
  };

  const resolvedRows = hasFilterParams ? searchState.searchResults : data;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedSumTotal = hasFilterParams ? (searchState.totalRow?.t_tt_nt ?? sumTotal) : sumTotal;

  const tables = [
    {
      name: 'Tất cả',
      rows: resolvedRows,
      columns: getSupplierReturnReceiptColumns(handleViewClick, resolvedSumTotal, isLoading)
    },
    {
      name: 'Lập chứng từ',
      rows: resolvedRows?.filter(row => row.status === '0'),
      columns: getSupplierReturnReceiptColumns(handleViewClick, resolvedSumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: resolvedRows?.filter(row => row.status === '3'),
      columns: getSupplierReturnReceiptColumns(handleViewClick, resolvedSumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Trả lại',
      rows: resolvedRows?.filter(row => row.status === '5'),
      columns: getSupplierReturnReceiptColumns(handleViewClick, resolvedSumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    },
    {
      name: 'Khác',
      rows: resolvedRows?.filter(row => row.status !== '0' && row.status !== '3' && row.status !== '5'),
      columns: getSupplierReturnReceiptColumns(handleViewClick, resolvedSumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-black' />
    }
  ];

  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      {showSearch && (
        <SearchDialog
          open={showSearch}
          onClose={handleCloseSearch}
          onSearch={handleSearchSubmit}
          formState={searchFieldState}
        />
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
          addItem={addItem}
          updateItem={updateItem}
          patchItem={patchItem}
          handleCloseForm={handleCloseForm}
          clearSelection={clearSelection}
          refreshData={refreshData}
        />
      )}

      <div className={`flex h-full flex-col ${showForm ? 'hidden' : ''}`}>
        <ActionBar
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
          onSearch={handleSearch}
          onRefresh={async () => {
            if (hasFilterParams) {
              await handleRefreshClick();
            } else if (isFiltered) {
              await searchData(searchParams);
            } else {
              await refreshData();
            }
            if (selectedObj) await fetchDetail(selectedObj.uuid);
          }}
          isEditDisabled={!selectedObj}
        />

        <ResizablePanelGroup direction='vertical' className='min-h-0 flex-1 overflow-hidden'>
          <ResizablePanel defaultSize={50} minSize={20}>
            <div className='h-full w-full overflow-hidden'>
              <ErpDataTableV2
                data-perf-id='pnhbtl-master-table'
                tables={tables}
                onRowClick={handleRowClick}
                selectedRowId={selectedRowIndex || undefined}
                totalItems={resolvedTotalItems}
                currentPage={resolvedCurrentPage}
                onSearchChange={handleSearchChange as BanHangRecord}
                onPageChange={onPageChange}
                loading={isLoading || isSearching || isFiltering}
              />
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle className='bg-slate-200/80 hover:bg-slate-300' />
          <ResizablePanel defaultSize={50} minSize={20}>
            <div className='h-full overflow-hidden'>
              <ErpInputTableV2
                data-perf-id='pnhbtl-bottom-detail-table'
                rows={selectedObj && !isLoading ? detail?.chi_tiet_data || [] : []}
                columns={getSupplierReturnReceiptDetailColumns}
                height={'100%'}
                showIndexColumn
                actionsDisabled={formMode === 'view'}
                actionOrder={DETAIL_ACTION_ORDER as BanHangRecord}
                actions={{ export: noop, pin: noop }}
                loading={isDetailLoading && Boolean(selectedObj)}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={() => {
            handleDelete();
            handleCloseDelete();
          }}
        />
      )}
      {showBulkDelete && (
        <BulkDeleteDialogV4
          onClose={handleCloseBulkDelete}
          title='Phiếu nhập hàng bán trả lại'
          handleDelete={deleteItem}
          columns={getSupplierReturnReceiptColumns(handleViewClick, resolvedSumTotal, isLoading)}
          dataRows={resolvedRows}
        />
      )}
    </div>
  );
}

export default function PhieuNhapHangBanTraLai() {
  return (
    <PhieuNhapHangBanTraLaiSearchProvider>
      <PhieuNhapHangBanTraLaiContent />
    </PhieuNhapHangBanTraLaiSearchProvider>
  );
}
